import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import { RecommendationBulkBar } from '@/components/recommendations/RecommendationBulkBar';
import {
  AdTypeCell,
  CampaignNameCell,
  DismissButton,
  EmptyTableMessage,
  formatPercent,
  formatRatio,
  SpActionButton,
} from '@/components/recommendations/ruleTables/ruleTableShared';
import { formatRecommendationCurrency, formatRecommendationCurrencyPrecise } from '@/api/recommendationFormat';
import type { RuleKeywordRow } from '@/types/ruleRecommendations';

export function PoorKeywordsTable({
  profileId,
  rows,
  selected,
  applying,
  onToggleSelect,
  onSelectAll,
  onDecreaseBid,
  onPause,
  onDismiss,
  onBulkApply,
  onBulkDismiss,
  onClearSelection,
}: {
  profileId: string;
  rows: RuleKeywordRow[];
  selected: Set<string>;
  applying: boolean;
  onToggleSelect: (keywordId: string) => void;
  onSelectAll: (ids: string[]) => void;
  onDecreaseBid: (row: RuleKeywordRow) => void;
  onPause: (row: RuleKeywordRow) => void;
  onDismiss: (row: RuleKeywordRow) => void;
  onBulkApply: () => void;
  onBulkDismiss: () => void;
  onClearSelection: () => void;
}) {
  if (!rows.length) {
    return <EmptyTableMessage message="No keywords match these rules for the selected range." />;
  }

  const selectable = rows.map((row) => row.keywordId);
  const allSelected = selectable.length > 0 && selectable.every((id) => selected.has(id));
  const someSelected = selectable.some((id) => selected.has(id));

  return (
    <>
      <div className="px-3 pt-2">
        <RecommendationBulkBar
          selectedCount={selected.size}
          onApplySelected={onBulkApply}
          onDismissSelected={onBulkDismiss}
          onClearSelection={onClearSelection}
          applying={applying}
        />
      </div>
      <TableWrap className="max-h-[28rem] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow className="divide-y-0 hover:bg-transparent">
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={() => onSelectAll(allSelected ? [] : selectable)}
                  aria-label="Select all poor-performing keywords"
                />
              </TableHead>
              <TableHead>Keyword</TableHead>
              <TableHead>Match type</TableHead>
              <TableHead>Campaign</TableHead>
              <TableHead>Ad type</TableHead>
              <TableHead className="text-right">Ad spend</TableHead>
              <TableHead className="text-right">Ad sales</TableHead>
              <TableHead className="text-right">Bid</TableHead>
              <TableHead className="text-right">ACoS</TableHead>
              <TableHead className="text-right">ROAS</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <tbody>
            {rows.map((row) => (
              <TableRow key={row.keywordId}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={selected.has(row.keywordId)}
                    onChange={() => onToggleSelect(row.keywordId)}
                    aria-label={`Select ${row.keywordText}`}
                  />
                </TableCell>
                <TableCell className="font-medium">{row.keywordText || row.keywordId}</TableCell>
                <TableCell>{row.matchType || '—'}</TableCell>
                <TableCell>
                  <CampaignNameCell
                    profileId={profileId}
                    campaignId={row.campaignId}
                    campaignName={row.campaignName}
                    adProduct={row.adProduct}
                  />
                </TableCell>
                <TableCell>
                  <AdTypeCell adProduct={row.adProduct} />
                </TableCell>
                <TableCell className="text-right font-mono">
                  {formatRecommendationCurrency(row.cost)}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {formatRecommendationCurrency(row.sales30d)}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {formatRecommendationCurrencyPrecise(row.currentBid)}
                </TableCell>
                <TableCell className="text-right font-mono">{formatPercent(row.acos)}</TableCell>
                <TableCell className="text-right font-mono">{formatRatio(row.roas)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    <SpActionButton
                      adProduct={row.adProduct}
                      onClick={() => onDecreaseBid(row)}
                      disabled={applying}
                    >
                      Decrease bid
                    </SpActionButton>
                    <SpActionButton adProduct={row.adProduct} onClick={() => onPause(row)} disabled={applying}>
                      Pause
                    </SpActionButton>
                    <DismissButton onClick={() => onDismiss(row)} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableWrap>
    </>
  );
}
